import { Stack } from '@mui/material'
import { useEffect, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { AppDispatch } from '../../store/store'
import { UserSelector } from '../../store/User'
import {
  WaterQualityIndexActions,
  WaterQualityIndexSelectors,
} from '../../store/waterQualityIndex'
import { AnchorCTA } from './Components/AnchorCTA'
import { ContactFaqSection } from './Components/ContactFaq'
import { DataCardsRow } from './Components/DataCards'
import { PlanSelectionSection } from './Components/PlanSelection'
import { TestMonitorProtectSection } from './Components/TestMonitorProtect'
import { VerdictCard } from './Components/VerdictCard'

export const RecommendationsPage = () => {
  const dispatch = useDispatch<AppDispatch>()

  const user = useSelector(UserSelector.user)

  const wqiOverall = useSelector(WaterQualityIndexSelectors.selectOverall)
  const waterSafetyStatus = useSelector(WaterQualityIndexSelectors.selectWaterSafetyStatus)
  const wqiUtility = useSelector(WaterQualityIndexSelectors.selectUtility)
  const topParameter = useSelector(WaterQualityIndexSelectors.selectTopParameter)

  // SIPP-817: anchor for the AnchorCTA to scroll to. Consumed by
  // <AnchorCTA/> (SIPP-818) and the plan selection section (SIPP-821).
  const planSectionRef = useRef<HTMLDivElement>(null)
  const scrollToPlanSection = () => {
    planSectionRef.current?.scrollIntoView({ behavior: 'smooth' })
  }

  // SIPP-611: fire WQI Report Completed to Klaviyo + Customer.io on first view.
  // useRef prevents double-fire within the session; backend checks klaviyoEventsFired
  // to prevent firing again on subsequent sessions.
  const wqiEventFiredRef = useRef(false)
  useEffect(() => {
    if (wqiEventFiredRef.current) return
    if (!user?._id || !wqiOverall) return

    wqiEventFiredRef.current = true
    dispatch(
      WaterQualityIndexActions.fireWqiReportCompletedAsync({
        userId: user._id,
        wqiScore: wqiOverall.score,
        wqiBand: waterSafetyStatus,
        topParameter,
        utilityName: wqiUtility?.name ?? '',
      })
    )
  }, [dispatch, user?._id, wqiOverall, waterSafetyStatus, topParameter, wqiUtility?.name])

  // Fetch WQI data when user is available
  useEffect(() => {
    if (user?._id) {
      dispatch(WaterQualityIndexActions.fetchWaterQualityIndexAsync({ userId: user._id }))
    }
  }, [dispatch, user?._id])

  // SIPP-823 audit: this page intentionally renders no navigation. The tab bar
  // (Overview / Recommendations / Utility / etc.) is provided by ProfileLayout's
  // <ProfileHeader /> above the <Outlet />, and sits above this content in both
  // mobile and desktop breakpoints.
  return (
    <Stack spacing={3} sx={{ py: 4, px: { xs: 2, md: 6 }, width: '100%' }}>
      {/* Section 1: Verdict */}
      <VerdictCard waterSafetyStatus={waterSafetyStatus} topParameter={topParameter} />

      {/* Section 1: Data Cards */}
      <DataCardsRow />

      {/* SIPP-829: AnchorCTA sits between the data summary and the action
          content so it acts as the natural divider into the plan flow. */}
      <AnchorCTA onScrollClick={scrollToPlanSection} />

      {/* Section 2: Test Monitor Protect */}
      <TestMonitorProtectSection />

      {/* Section 3: Plan Selection */}
      <PlanSelectionSection ref={planSectionRef} />

      {/* Section 4: Contact + FAQ */}
      <ContactFaqSection />
    </Stack>
  )
}
